import type { Session } from '@diana/contracts';
import { Globe, FileText } from 'lucide-react';
import type { ReactNode } from 'react';

export function enquiryPage(sourcePath: string) {
  let url: URL;
  try {
    url = new URL(sourcePath, 'https://new.perova.photo');
  } catch {
    return null;
  }
  // Stored browser context is untrusted; never turn a protocol-relative path into an external link.
  if (url.origin !== 'https://new.perova.photo') return null;
  const path = url.pathname.replace(/\.html$/, '').replace(/\/+$/, '') || '/';
  const names: Record<string, string> = {
    '/': 'Home',
    '/weddings': 'Weddings',
    '/proposals': 'Proposals',
    '/pricing': 'Pricing',
    '/portfolio': 'Portfolio',
    '/about': 'About',
    '/journal': 'Journal',
    '/contact': 'Contact',
  };
  return { href: url.href, label: names[path] ?? path };
}

export function inquiryRows(context: Session['inquiryContext']) {
  if (!context) return [];
  const groups = [
    {
      label: 'Source',
      parts: [
        ['UTM source', context.campaignSource],
        ['UTM medium', context.campaignMedium],
        ['Referrer', context.referrerHost],
      ],
    },
    { label: 'Campaign', parts: [['UTM campaign', context.campaignName]] },
    {
      label: 'Visitor',
      parts: [
        ['Browser locale', context.visitorLanguage],
        ['Time zone', context.visitorTimeZone],
        ['Device', context.visitorDevice],
      ],
    },
  ];
  return groups
    .map(({ label, parts }) => ({
      label,
      parts: parts.filter((part): part is [string, string] => Boolean(part[1]?.trim())),
    }))
    .filter((group) => group.parts.length > 0);
}

export function InquiryDetails({
  sourcePath,
  context,
  children,
}: {
  sourcePath?: string | null;
  context: Session['inquiryContext'];
  children?: ReactNode;
}) {
  const page = sourcePath ? enquiryPage(sourcePath) : null;
  return (
    <>
      {page ? (
        <div className="shoot-detail">
          <FileText size={19} aria-hidden="true" />
          <dt>Page</dt>
          <dd>
            <a href={page.href} target="_blank" rel="noreferrer">
              {page.label}
            </a>
          </dd>
        </div>
      ) : null}
      {children}
      {inquiryRows(context).map(({ label, parts }) => (
        <div className="shoot-detail shoot-detail--inquiry" key={label}>
          <Globe size={19} aria-hidden="true" />
          <dt>{label}</dt>
          <dd className="shoot-inquiry-values">
            {parts.map(([name, value], index) => (
              <span key={name}>
                {index > 0 ? (
                  <span aria-hidden="true" className="shoot-inquiry-separator">
                    {' '}
                    ·{' '}
                  </span>
                ) : null}
                <span title={name} aria-label={`${name}: ${value}`}>
                  {value}
                </span>
              </span>
            ))}
          </dd>
        </div>
      ))}
    </>
  );
}
